import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import { Layout } from '@/components/layout';
import { Link } from '@/components/links';
import { StackCards } from '@/components/StackCards';
import { Card } from '@/components/StackCards/types';
import IconInstallments from '@/assets/icons/heroIcons/Installments.svg';
import IconThird from '@/assets/icons/heroIcons/Icon-third.svg';
import IconCard from '@/assets/icons/heroIcons/Card-front.svg';
import IconSearch from '@/assets/icons/heroIcons/Search.svg';
import IconCursor from '@/assets/icons/heroIcons/Cursor-click.svg';
import IconPayment from '@/assets/icons/heroIcons/Deferred-billing-payment.svg';
import Hero from '@/sections/Hero';
import MoreInfoSection from '@/sections/MoreInfo';
import { Calculator } from '@/sections/installments/Calculator';
import { FaqIntro } from '@/sections/FaqIntro';
import { Button } from '@/components/buttons';
import { InfoBoxes } from '@/components/boxes';
import { SectionHeadline } from '@/components/SectionHeadline';
import { type IntlProps, getIntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { Trans, useTranslation } from 'react-i18next';
import { LINKS } from '@/constants/links';
import React from 'react';
import { mergeClasses } from '@/utils/style';
import { NumericRange } from '@/types/range';
import { getPageBannerBySlug, PageBanner } from '@/api/sanity/pageBanners';
import { SeoHeader } from '@/components/SeoHeader';
import { Text } from '@/components/typography';
import { isAllowedRoute } from '@/lang/pagesConfig';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import { AnimatedSection, AnimatedContent } from '@/components/animation';
import useMediaQuery from '@/hooks/useMediaQuery';
import { getMediaQuery } from '@/styles/sizes';
import { useClientOnly } from '@/hooks/useClientOnly';
import IconMaxi from '@/assets/icons/heroIcons/Maxi.svg';
import { getSectionAnchorId } from '@/utils/anchors';
import { useProductRegistrationButtons, useProductRegistrationLink } from '@/hooks/useProductRegistrationLink';
interface Props extends IntlProps {
  pageBanner: PageBanner | null;
  menuPromoBanners?: PageMenuPromoBanner[];
}
export default function Page(props) {
  const $ = _c(148);
  usePageView("loan");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["loan", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    pageBanner,
    menuPromoBanners
  } = props;
  const registrationLink = useProductRegistrationLink("pujcka");
  const heroButtons = useProductRegistrationButtons(pageBanner?.buttons, "pujcka");
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getMediaQuery("md");
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isDesktop = useMediaQuery(t1);
  const {
    isClient
  } = useClientOnly();
  let t2;
  if ($[2] !== registrationLink || $[3] !== t) {
    t2 = Array.from({
      length: 5
    }, (_, index) => {
      const cardIndex = index + 1 as NumericRange<1, 5>;
      return {
        index,
        menuTitle: "",
        title: <div className={mergeClasses(cardIndex === 5 && "text-center lg:text-left")}>{t(`howItWorks.items.${cardIndex}.title`)}</div>,
        ...(cardIndex < 5 ? {
          description: <Trans t={t} i18nKey={`howItWorks.items.${cardIndex}.text`}><Link to={LINKS().packages} testId="loanPackages" analyticsParam="loanPackages" /></Trans>
        } : {
          footer: <Button analyticsParam="loanRegistration" testId="loanRegistration" variant="primary" size="lg" className="m-auto w-full md:w-auto lg:m-0" to={registrationLink}>{t(`howItWorks.items.${cardIndex}.text`)}</Button>
        }),
        images: {
          desktop: {
            src: `/images/sections/loan/cards/desktop/cs/${cardIndex}.png`,
            alt: t(`howItWorks.items.${cardIndex}.title`)
          },
          mobile: {
            src: `/images/sections/loan/cards/mobile/cs/${cardIndex}.png`,
            alt: t(`howItWorks.items.${cardIndex}.title`)
          },
          ...(cardIndex < 5 && {
            variant: "appMockup"
          })
        }
      };
    });
    $[2] = registrationLink;
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const cards = t2;
  let t3;
  if ($[5] !== t) {
    t3 = t("seo.title");
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t) {
    t4 = t("seo.description");
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <SeoHeader title={t3} description={t4} />;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = pageBanner?.videoId;
  let t7;
  if ($[12] !== pageBanner?.title || $[13] !== t) {
    t7 = pageBanner?.title ?? t("banner.title");
    $[12] = pageBanner?.title;
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== pageBanner?.text || $[16] !== t) {
    t8 = pageBanner?.text ?? t("banner.text");
    $[15] = pageBanner?.text;
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== heroButtons || $[19] !== registrationLink || $[20] !== t) {
    t9 = heroButtons ?? [{
      ariaLabel: t("banner.cta"),
      label: t("banner.cta"),
      to: registrationLink,
      testId: "loansHeroPrimaryCTA",
      analyticsParam: "loansHeroPrimaryCTA",
      variant: "primary"
    }];
    $[18] = heroButtons;
    $[19] = registrationLink;
    $[20] = t;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const t10 = pageBanner?.image?.url ?? "/images/sections/loan/hero/cs/hero.png";
  let t11;
  if ($[22] !== pageBanner?.image?.alt || $[23] !== t) {
    t11 = pageBanner?.image?.alt ?? t("banner.title");
    $[22] = pageBanner?.image?.alt;
    $[23] = t;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t11) {
    t12 = {
      url: t10,
      alt: t11
    };
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const t13 = pageBanner?.variant || "mockupMobile";
  let t14;
  if ($[28] !== t12 || $[29] !== t13 || $[30] !== t6 || $[31] !== t7 || $[32] !== t8 || $[33] !== t9) {
    t14 = <Hero videoId={t6} title={t7} text={t8} buttons={t9} image={t12} variant={t13} className="bg-blue-50" />;
    $[28] = t12;
    $[29] = t13;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = getSectionAnchorId("advantages");
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t) {
    t16 = t("advantages.title");
    $[36] = t;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t16) {
    t17 = <SectionHeadline className="mx-auto mb-10 max-w-192 px-0 md:mb-16" anchorKey="advantages.title">{t16}</SectionHeadline>;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t) {
    t18 = t("advantages.items.1.title");
    $[40] = t;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Link to={LINKS().packages} testId="loanPackages" analyticsParam="loanPackages" />;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t) {
    t20 = <Trans t={t} i18nKey="advantages.items.1.text">{t19}</Trans>;
    $[43] = t;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <IconCursor />;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t18 || $[47] !== t20) {
    t22 = {
      title: t18,
      paragraph: t20,
      icon: t21
    };
    $[46] = t18;
    $[47] = t20;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== t) {
    t23 = t("advantages.items.2.title");
    $[49] = t;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t) {
    t24 = t("advantages.items.2.text");
    $[51] = t;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <IconSearch />;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== t23 || $[55] !== t24) {
    t26 = {
      title: t23,
      paragraph: t24,
      icon: t25
    };
    $[54] = t23;
    $[55] = t24;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] !== t) {
    t27 = t("advantages.items.3.title");
    $[57] = t;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== t) {
    t28 = t("advantages.items.3.text");
    $[59] = t;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <IconPayment />;
    $[61] = t29;
  } else {
    t29 = $[61];
  }
  let t30;
  if ($[62] !== t27 || $[63] !== t28) {
    t30 = {
      title: t27,
      paragraph: t28,
      icon: t29
    };
    $[62] = t27;
    $[63] = t28;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  if ($[65] !== t22 || $[66] !== t26 || $[67] !== t30) {
    t31 = <InfoBoxes items={[t22, t26, t30]} variant="basic" />;
    $[65] = t22;
    $[66] = t26;
    $[67] = t30;
    $[68] = t31;
  } else {
    t31 = $[68];
  }
  let t32;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <Link to={`${LINKS().packages}#mini`} testId="loanPackages" analyticsParam="loanPackages" />;
    $[69] = t32;
  } else {
    t32 = $[69];
  }
  let t33;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <Link to={LINKS().revolving} testId="loanRevolving" analyticsParam="loanRevolving" />;
    $[70] = t33;
  } else {
    t33 = $[70];
  }
  let t34;
  if ($[71] !== t) {
    t34 = <Text variant="p4" className="text-center text-gray-800"><Trans t={t} i18nKey="advantages.text">{t32}{t33}</Trans></Text>;
    $[71] = t;
    $[72] = t34;
  } else {
    t34 = $[72];
  }
  let t35;
  if ($[73] !== t) {
    t35 = t("advantages.cta");
    $[73] = t;
    $[74] = t35;
  } else {
    t35 = $[74];
  }
  let t36;
  if ($[75] !== registrationLink || $[76] !== t35) {
    t36 = <Button to={registrationLink} testId="postponeDueDateRegistration" analyticsParam="postponeDueDateRegistration" size="lg" variant="primary" align="center">{t35}</Button>;
    $[75] = registrationLink;
    $[76] = t35;
    $[77] = t36;
  } else {
    t36 = $[77];
  }
  let t37;
  if ($[78] !== t31 || $[79] !== t34 || $[80] !== t36) {
    t37 = <div className="flex flex-col gap-10 px-4 md:px-0 lg:gap-12">{t31}{t34}{t36}</div>;
    $[78] = t31;
    $[79] = t34;
    $[80] = t36;
    $[81] = t37;
  } else {
    t37 = $[81];
  }
  let t38;
  if ($[82] !== t17 || $[83] !== t37) {
    t38 = <div className="relative z-10">{t17}{t37}</div>;
    $[82] = t17;
    $[83] = t37;
    $[84] = t38;
  } else {
    t38 = $[84];
  }
  let t39;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <div className="bgCircle absolute top-12 right-0 h-[11.25rem] w-[11.25rem] translate-x-1/2 lg:top-0 lg:left-0 lg:h-125 lg:w-125 lg:-translate-x-1/4" />;
    $[85] = t39;
  } else {
    t39 = $[85];
  }
  let t40;
  if ($[86] !== t38) {
    t40 = <AnimatedSection bgColor="bg-blue-100" id={t15}><AnimatedContent className="overflow-x-hidden">{t38}{t39}</AnimatedContent></AnimatedSection>;
    $[86] = t38;
    $[87] = t40;
  } else {
    t40 = $[87];
  }
  let t41;
  if ($[88] !== isClient || $[89] !== isDesktop) {
    t41 = isClient && <AnimatedSection bgColor={isDesktop ? "bg-blue-700" : "bg-blue-900"} id={getSectionAnchorId("calculator")}><AnimatedContent className="overflow-x-hidden"><Calculator type="loan" /></AnimatedContent></AnimatedSection>;
    $[88] = isClient;
    $[89] = isDesktop;
    $[90] = t41;
  } else {
    t41 = $[90];
  }
  let t42;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = getSectionAnchorId("howItWorks.title");
    $[91] = t42;
  } else {
    t42 = $[91];
  }
  let t43;
  if ($[92] !== t) {
    t43 = t("howItWorks.title");
    $[92] = t;
    $[93] = t43;
  } else {
    t43 = $[93];
  }
  let t44;
  if ($[94] !== t43) {
    t44 = <div className="mx-auto max-w-192"><SectionHeadline className="mb-10 md:mb-16" anchorKey="howItWorks.title">{t43}</SectionHeadline></div>;
    $[94] = t43;
    $[95] = t44;
  } else {
    t44 = $[95];
  }
  let t45;
  if ($[96] !== cards) {
    t45 = <StackCards imagePosition="left" cards={cards} />;
    $[96] = cards;
    $[97] = t45;
  } else {
    t45 = $[97];
  }
  let t46;
  if ($[98] !== t44 || $[99] !== t45) {
    t46 = <AnimatedContent>{t44}{t45}</AnimatedContent>;
    $[98] = t44;
    $[99] = t45;
    $[100] = t46;
  } else {
    t46 = $[100];
  }
  let t47;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = LINKS();
    $[101] = t47;
  } else {
    t47 = $[101];
  }
  let t48;
  if ($[102] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <Link to="/caste-dotazy/dalsi-caste-otazky" analyticsParam="faqLoan" testId="faqLoan" />;
    $[102] = t48;
  } else {
    t48 = $[102];
  }
  let t49;
  if ($[103] !== t) {
    t49 = <AnimatedContent className="pt-20 pb-10 lg:pt-40 lg:pb-20"><FaqIntro link={t47.faq} text={<Trans i18nKey="faq.text" t={t}>{t48}</Trans>} /></AnimatedContent>;
    $[103] = t;
    $[104] = t49;
  } else {
    t49 = $[104];
  }
  let t50;
  if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <IconInstallments className="fillButtonIcon" />;
    $[105] = t50;
  } else {
    t50 = $[105];
  }
  let t51;
  if ($[106] !== t) {
    t51 = t("core:products.installments");
    $[106] = t;
    $[107] = t51;
  } else {
    t51 = $[107];
  }
  let t52;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = LINKS();
    $[108] = t52;
  } else {
    t52 = $[108];
  }
  let t53;
  if ($[109] !== t51) {
    t53 = {
      analyticsParam: "loanMoreInfoDeferredPayment",
      icon: t50,
      testId: "loanMoreInfoDeferredPayment",
      title: t51,
      to: t52.installments
    };
    $[109] = t51;
    $[110] = t53;
  } else {
    t53 = $[110];
  }
  let t54;
  if ($[111] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = <IconMaxi className="fillButtonIcon" />;
    $[111] = t54;
  } else {
    t54 = $[111];
  }
  let t55;
  if ($[112] !== t) {
    t55 = t("core:products.revolving");
    $[112] = t;
    $[113] = t55;
  } else {
    t55 = $[113];
  }
  let t56;
  if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = LINKS();
    $[114] = t56;
  } else {
    t56 = $[114];
  }
  let t57;
  if ($[115] !== t55) {
    t57 = {
      analyticsParam: "loanMoreInfoRevolvingApplication",
      icon: t54,
      testId: "loanMoreInfoRevolvingApplication",
      title: t55,
      to: t56.revolving
    };
    $[115] = t55;
    $[116] = t57;
  } else {
    t57 = $[116];
  }
  let t58;
  if ($[117] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <IconThird className="fillButtonIcon" />;
    $[117] = t58;
  } else {
    t58 = $[117];
  }
  let t59;
  if ($[118] !== t) {
    t59 = t("core:products.payInThree");
    $[118] = t;
    $[119] = t59;
  } else {
    t59 = $[119];
  }
  let t60;
  if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
    t60 = LINKS();
    $[120] = t60;
  } else {
    t60 = $[120];
  }
  let t61;
  if ($[121] !== t59) {
    t61 = {
      analyticsParam: "loanMoreInfoPayInThree",
      icon: t58,
      testId: "loanMoreInfoPayInThree",
      title: t59,
      to: t60.payInThree
    };
    $[121] = t59;
    $[122] = t61;
  } else {
    t61 = $[122];
  }
  let t62;
  if ($[123] === Symbol.for("react.memo_cache_sentinel")) {
    t62 = <IconCard className="fillButtonIcon" />;
    $[123] = t62;
  } else {
    t62 = $[123];
  }
  let t63;
  if ($[124] !== t) {
    t63 = t("core:products.card");
    $[124] = t;
    $[125] = t63;
  } else {
    t63 = $[125];
  }
  let t64;
  if ($[126] === Symbol.for("react.memo_cache_sentinel")) {
    t64 = LINKS();
    $[126] = t64;
  } else {
    t64 = $[126];
  }
  let t65;
  if ($[127] !== t63) {
    t65 = {
      analyticsParam: "loanMoreInfoCard",
      icon: t62,
      testId: "loanMoreInfoCard",
      title: t63,
      to: t64.card
    };
    $[127] = t63;
    $[128] = t65;
  } else {
    t65 = $[128];
  }
  let t66;
  if ($[129] !== t53 || $[130] !== t57 || $[131] !== t61 || $[132] !== t65) {
    t66 = <AnimatedContent className="relative z-10"><MoreInfoSection buttons={[t53, t57, t61, t65]} /></AnimatedContent>;
    $[129] = t53;
    $[130] = t57;
    $[131] = t61;
    $[132] = t65;
    $[133] = t66;
  } else {
    t66 = $[133];
  }
  let t67;
  if ($[134] === Symbol.for("react.memo_cache_sentinel")) {
    t67 = <div className="bgCircle absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 rotate-180" />;
    $[134] = t67;
  } else {
    t67 = $[134];
  }
  let t68;
  if ($[135] !== t49 || $[136] !== t66) {
    t68 = <div className="relative overflow-hidden">{t49}{t66}{t67}</div>;
    $[135] = t49;
    $[136] = t66;
    $[137] = t68;
  } else {
    t68 = $[137];
  }
  let t69;
  if ($[138] !== t46 || $[139] !== t68) {
    t69 = <AnimatedSection bgColor="bg-blue-100" className="pb-0!" id={t42}>{t46}{t68}</AnimatedSection>;
    $[138] = t46;
    $[139] = t68;
    $[140] = t69;
  } else {
    t69 = $[140];
  }
  let t70;
  if ($[141] !== menuPromoBanners || $[142] !== t14 || $[143] !== t40 || $[144] !== t41 || $[145] !== t5 || $[146] !== t69) {
    t70 = <Layout menuPromoBanners={menuPromoBanners}>{t5}{t14}{t40}{t41}{t69}</Layout>;
    $[141] = menuPromoBanners;
    $[142] = t14;
    $[143] = t40;
    $[144] = t41;
    $[145] = t5;
    $[146] = t69;
    $[147] = t70;
  } else {
    t70 = $[147];
  }
  return t70;
}
export const getStaticProps = (async ctx => {
  const isAllowed = isAllowedRoute('pujcka');
  if (!isAllowed) {
    return {
      notFound: true
    };
  }
  return {
    props: {
      pageBanner: await getPageBannerBySlug('loan'),
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      ...(await getIntlProps(ctx, ['core', 'loan']))
    }
  };
}) satisfies GetStaticProps<Props>;